import {
    STAxPos,
    STBarDir,
    STBuiltInUnit,
    STCrossBetween,
    STOrientationDmlC,
    STTickLblPos,
    STTickMark,
    SubChartType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import _set from 'lodash/set';
import Decimal from 'decimal.js';
import { getTextInParagraphList } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import isEmpty from 'lodash/isEmpty';
import i18n from '@i18next/i18n';
import {
    getFormattedNumber,
    replaceCategoryMark,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';
import { fmtIsDate } from '@tencent/tdocs-format-render';
import {
    createDocElement, createDocumentWithChartTextProps,
    getTextStyle, updateDocElementText,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/text';
import { generateCustomStyles, getOriginStyle, getShapeAttr } from '../../utils/style';
import { DEFAULT_AXIS_LINE_COLOR, defaultValues } from '../../const';
import { axisUnitLabelMap } from '../map';
import type {
    IBarChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/bar-chart.interface';
import type { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import type {
    IAxisTitleProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis-title.interface';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import type { IAxisProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type { IChartAxisOption, IChartScale } from '@tencent/chart/lib/chart-ui/interface';
import type { AxisCfg, AxisGridCfg } from '@tencent/chart/lib/antv/g2/interface';
import { ScaleType } from '@tencent/chart/lib/antv/g2/interface';
import { ShapeAttrs } from '@tencent/chart/lib/antv/g2';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import isNil from 'lodash/isNil';

export class Axis {
    public static getAxisConfigWithTitle(
        context: ChartContext,
        axisConf: IChartAxisOption,
        title: IAxisTitleProperty,
    ): IChartAxisOption {
        const style = getTextStyle(title?.text ?? title, {}) as ShapeAttrs;
        const titleText = getTextInParagraphList(title?.text?.paragraphList) || i18n.t('坐标轴标题');
        const docElement = createDocumentWithChartTextProps(title);
        const titleConfig: AxisCfg['title'] = {
            style: {
                ...style,
                customStyles: { text: docElement, ...generateCustomStyles(getOriginStyle(title?.props)) },
            },
            text: titleText,
        };

        return {
            ...axisConf as AxisCfg,
            title: {
                ...titleConfig,
                offset: 130,
            },
        };
    }

    public xAxisProperty?: IAxisProperty;
    public yAxisProperty?: IAxisProperty;
    protected xScale?: IChartScale;    // scale的结果，要作为获取getAxisOptions的前置逻辑，所以用变量缓存一下。yScale同理
    protected yScale?: IChartScale;

    public constructor(protected subChart: IBaseSubChartProperty, protected chartModel: IChartModel) {
        const { xAxis, yAxis } = subChart;
        this.xAxisProperty = xAxis;
        this.yAxisProperty = yAxis;
    }

    public init() {
        this.xScale = this.getXScale();
        this.yScale = this.getYScale();

        return this;
    }

    /**
     * 获取Chart库所需xAxis和yAxis参数
     */
    public getAxisConfig(): { xAxis?: IChartAxisOption; yAxis?: IChartAxisOption} {
        return {
            xAxis: this.getAxisOptions(this.xAxisProperty, 'xAxis', this.subChart.type),
            yAxis: this.getAxisOptions(this.yAxisProperty, 'yAxis', this.subChart.type),
        };
    }

    /**
     * 获取Chart库所需xScale和yScale参数
     */
    public getScaleConfig(): { xScale?: IChartScale; yScale?: IChartScale } {
        return {
            xScale: this.xScale,
            yScale: this.yScale,
        };
    }

    protected getXScale(): IChartScale | undefined {
        const { yAxisProperty: yAxis, xAxisProperty: xAxis } = this;
        // x轴的crossBetween的设置在y轴上
        let xScale: IChartScale = {
            range: yAxis?.crossBetween === STCrossBetween.midCat ? [0, 1] : undefined,
        };

        if (xAxis?.type === 'date') {
            // 序列化data时，如果是日期类型的category，已经排序处理过了
            const data = (this.subChart.data?.map?.((item) => {
                if (item.category !== '') {
                    return Number(item.category);
                }
                return undefined;
            }) || []).filter(i => !isNil(i)) as number[];
            const dataMin = Math.min(...data);
            const dataMax = Math.max(...data);
            const { max: scaleMax = dataMax, min: scaleMin = dataMin } = xAxis.scaling || {};
            xScale = {
                ...xScale,
                maxLimit: scaleMax,
                minLimit: scaleMin,
                useDefaultScaleRange: true,
            };
        }

        return xScale;
    }

    protected getYScale(): IChartScale | undefined {
        const { yAxisProperty: yAxis, subChart } = this;
        const { series } = subChart;
        if (!subChart || !yAxis || !series.length) return;
        const { grouping } = subChart as any;

        if (subChart.isPercentStackedGrouping(grouping)) {
            return {
                formatPattern: yAxis.numFmt?.formatCode ?? '0%',
            };
        }

        const yAxisScale = yAxis.scale;
        const { logBase } = yAxis.scaling || {};
        const yScale = {
            type: logBase ? 'log' as ScaleType : undefined,
        };

        if (yAxisScale) {
            const { max, min } = yAxisScale;
            let scale: IChartScale = {
                ...yScale,
                max,
                min,
                ticks: yAxis.ticks,
            };
            if (subChart.isStackedGrouping(grouping)) {
                scale = {
                    ...scale,
                    maxLimit: max,
                    minLimit: min,
                };
            }
            return scale;
        }

        return yScale;
    }

    // eslint-disable-next-line complexity
    protected getAxisOptions(axisProps: IAxisProperty | undefined, type: 'xAxis' | 'yAxis', chartType?: SubChartType): IChartAxisOption | undefined {
        if (!axisProps) return;

        let finalAxisProps;
        const isEmptyTextStyle = !axisProps?.paragraphList;
        const anotherOverlayAxis = this.findOverlayAxis(axisProps);
        // 这里是一个trick。当组合坐标轴一起显示的时候，如果需要继承属性，slide和office的属性对不上会导致坐标轴文本不重合有重影
        // 所以当该坐标轴没有paragraphList，就直接继承相同位置坐标轴的文本样式，防止stylelist中的属性对不上。
        if (isEmptyTextStyle && anotherOverlayAxis) {
            finalAxisProps = { paragraphList: anotherOverlayAxis.paragraphList };
        } else {
            finalAxisProps = axisProps;
        }

        const textStyle = getTextStyle(finalAxisProps, {});
        const docElement = createDocElement(undefined, finalAxisProps, '2.0', { defaultLineHeight: 1 });
        const style = {
            ...textStyle,
            customStyles: { text: docElement, ...generateCustomStyles(getOriginStyle(finalAxisProps?.props, { filter: ['outline'] })) },
        }; // 坐标轴label不显示边框
        const { border, outline, effects } = getShapeAttr(axisProps.props);
        const axisConf: {[props: string]: any} = { id: axisProps.axId };

        // 坐标轴线
        // 坐标轴删除时，坐标轴线、刻度线、label、label显示单位都不展示
        // 雷达图坐标轴被删除时仍然需要显示
        if ((axisProps.isDelete && this.subChart.type !== SubChartType.RadarChart)
            || !this.hasBorder(axisProps?.props)) {
            axisConf.line = null;
        } else if (chartType !== SubChartType.RadarChart) {
            const newOutline = outline ? outline : { color: DEFAULT_AXIS_LINE_COLOR, width: 1 };
            axisConf.line = {
                style: {
                    stroke: DEFAULT_AXIS_LINE_COLOR, // 自动填充颜色
                    ...border,
                    customStyles: generateCustomStyles({ outline: newOutline, effects }),
                },
            };
        }

        // 刻度线
        let subTickCount;
        if (axisProps.type === 'value') {
            subTickCount = 4;
            if (this.yAxisProperty?.scale) {
                subTickCount = (Math
                    .ceil(this.yAxisProperty.scale?.majorUnit / this.yAxisProperty.scale?.minorUnit) || 5) - 1;
            }
        } else if (axisProps.type === 'category') {
            subTickCount = 1;
        } else if (axisProps.type === 'date') {
            subTickCount = 0;
        }

        axisConf.tickLine = null;
        axisConf.subTickLine = null;
        if (axisConf.line !== null) {
            // 主刻度线
            if (axisProps.majorTickMark !== STTickMark.none) {
                axisConf.tickLine = {
                    style: { ...border, customStyles: generateCustomStyles({ outline }) },
                    length: style.fontSize * 0.35 || defaultValues.AXIS_TICK_LINE_LENGTH,
                    tickMark: this.getTickMark(axisProps.majorTickMark),
                };
                if (type === 'xAxis' && this.yAxisProperty?.crossBetween === STCrossBetween.between) {
                    axisConf.tickLine.alignTick = false;
                }
            }

            // 子刻度线
            if (axisProps.minorTickMark && axisProps.minorTickMark !== STTickMark.none) {
                axisConf.subTickLine = {
                    style: { ...border, outline },
                    count: subTickCount,
                    length: style.fontSize * 0.35 * 0.75 || defaultValues.AXIS_SUB_TICK_LINE_LENGTH,
                    tickMark: this.getTickMark(axisProps.minorTickMark),
                };
            }
        }

        // label
        axisConf.label = !axisProps.isDelete ? this.getAxisLabel(axisProps, style) : null;

        // 坐标轴标题
        if (axisProps.title) {
            axisConf.title = this.getAxisTitle(axisProps.title);
        }

        // 显示单位 label
        // 显示单位和坐标轴标题同时存在时，这里优先显示单位， 坐标轴标题通过createAxisTitleConf实现
        if (!axisProps.isDelete && !isEmpty(axisProps.units?.label)) {
            axisConf.title = this.getAxisUnitLabel(axisProps.units);
        }

        // 网格线
        axisConf.grid = this.getGrid(axisProps.majorGridlines, type);
        const subGrid = this.getGrid(axisProps.minorGridlines, type);
        axisConf.subGrid = subGrid ? { ...subGrid, count: subTickCount } : subGrid;

        // 坐标轴位置
        const crossAxis = type === 'xAxis' ? this.yAxisProperty : this.xAxisProperty;
        const allNegative = !this.subChart.data.some(data => data.value >= 0);
        axisConf.position = this.getAxisPosition(
            axisProps.position!,
            crossAxis?.scaling?.orientation === STOrientationDmlC.maxMin, allNegative,
        );

        return axisConf;
    }

    /**
     * 获取坐标轴文本相关的配置参数
     */
    protected getAxisLabel(axisProps: IAxisProperty, style): AxisCfg['label']  {
        let formatCode;
        const isCatOrDateAx = axisProps.type === 'category' || axisProps.type === 'date';
        if (isCatOrDateAx) {
            // 定位不到serires, 所以用series[0]
            formatCode = this.subChart.series[0]?.categoryFormatCode || axisProps.numFmt?.formatCode;
        } else {
            formatCode = axisProps.numFmt?.formatCode;
        }

        // 是否是条形图
        const isBarChart = this.subChart.type === SubChartType.BarChart
            && (this.subChart as IBarChartProperty).direction === STBarDir.bar;

        return axisProps.tickLabelPosition === STTickLblPos.none ? null : {
            autoRotate: axisProps.type !== 'value'
                && !fmtIsDate(formatCode || '') && !isBarChart,
            style,
            offset: (style.fontSize
                + (((axisProps.lblOffset ?? defaultValues.AXIS_LABEL_OFFSET) / 100) * style.fontSize / 2)) / 2,
            formatter: (value: string | number, item, index, style: any) => {
                if (typeof value === 'string') {
                    value = replaceCategoryMark(value);
                }
                if (axisProps.units?.unit && !isNaN(Number(value))) {
                    value = Decimal.mul(value, axisUnitLabelMap[axisProps.units.unit]?.value || 1).valueOf();
                }

                let text;
                // 如果是catAx的label，就优先取categoryPoints的formatCode
                // cat大部分是字符串，有很多需要格式化的情况比如日期
                if (isCatOrDateAx) {
                    // 因为所有的series的category都是一样的，这里直接取第一个series的category
                    const categoryPoints = this.subChart.series[0].dataList.map(item => item.categoryPoint);
                    text = getFormattedNumber(value, categoryPoints[index]?.formatCode || formatCode);
                }
                // valueAx一般是数字，需要格式化的时候也一般都是通用的格式化，所以直接用axisProps上的formatCode（而且这里也拿不到确切的valuePoint, 因为获取不到准备的series）
                text = getFormattedNumber(value, formatCode);
                if (style?.customStyles?.text) {
                    // label样式都是一样的，但文本不一样，这里必须拷贝一份
                    style.customStyles = { ...style?.customStyles, text: deepCopy(style.customStyles.text) };
                    updateDocElementText(style.customStyles.text, text);
                }
                return text;
            },
        };
    }

    /**
     * 获取坐标轴单位标签相关的配置参数
     */
    protected getAxisUnitLabel(units: IAxisProperty['units']): AxisCfg['title'] {
        const style = getTextStyle(units?.label, {}) as ShapeAttrs;
        const content = axisUnitLabelMap[units?.unit || STBuiltInUnit.PBDEFAULT]?.text;
        const docElement = createDocElement(units?.label?.text, units?.label, content);
        return {
            style: {
                ...style,
                customStyles: { text: docElement, ...generateCustomStyles(getOriginStyle(units?.label?.props)) },
            },
            text: content,
            position: 'end',
        };
    }

    /**
     * 获取坐标轴标题相关的配置参数
     */
    protected getAxisTitle(title: IAxisTitleProperty): AxisCfg['title'] {
        const style = getTextStyle(title?.text ?? title, {}) as ShapeAttrs;
        const titleText = getTextInParagraphList(title?.text?.paragraphList) || i18n.t('坐标轴标题');
        const docElement = createDocumentWithChartTextProps(title);
        return {
            style: {
                ...style,
                customStyles: { text: docElement, ...generateCustomStyles(getOriginStyle(title?.props)) },
            },
            text: titleText,
        };
    }

    /**
     * 获取网格线相关的配置参数
     */
    protected getGrid(gridlines, type: 'xAxis' | 'yAxis'): AxisGridCfg | null {
        if (!gridlines || gridlines?.props?.style?.border?.noFill) {
            return null;
        }

        const grid: AxisGridCfg = {};
        if (type === 'xAxis') {
            grid.alignTick = this.yAxisProperty?.crossBetween !== STCrossBetween.between;
        }
        const { border, outline, effects } = getShapeAttr({
            style: {
                ...gridlines?.props.style,
                border: {
                    ...gridlines?.props.style.border,
                },
            },
        });
        const defaultOriginLineStyle = { color: DEFAULT_AXIS_LINE_COLOR, width: 1 };

        _set(grid, 'line', {
            style: {
                ...border,
                // Todo 主题样式中有phClr时，颜色显示不对，这里先用默认颜色
                stroke: border?.stroke || DEFAULT_AXIS_LINE_COLOR,
                customStyles: generateCustomStyles({ effects, outline: {  ...defaultOriginLineStyle,  ...outline } }),
            },
        });
        return grid;
    }

    protected hasBorder(props) {
        if (props?.style?.border && isEmpty(props.style.border)) return false;
        return true;
    }

    /**
     * 获取坐标轴位置
     */
    protected getAxisPosition(position: STAxPos, reflect: boolean, allNegative?): boolean {
        const positionMap = reflect ? {
            [STAxPos.l]: 'right',
            [STAxPos.r]: 'left',
            [STAxPos.b]: allNegative ? 'bottom' : 'top',
            [STAxPos.t]: allNegative ? 'top' : 'bottom',
        } : {
            [STAxPos.l]: 'left',
            [STAxPos.r]: 'right',
            [STAxPos.b]: allNegative ? 'top' : 'bottom',
            [STAxPos.t]: allNegative ? 'bottom' : 'top',
        };

        return positionMap[position];
    }

    protected getTickMark(tickMark?: STTickMark) {
        const tickMarkMap = {
            [STTickMark.cross]: 'cross',
            [STTickMark.in]: 'in',
            [STTickMark.out]: 'out',
            [STTickMark.PBDEFAULT]: 'none',
            [STTickMark.none]: 'none',
        };
        return tickMarkMap[tickMark || STTickMark.none];
    }


    /**
     * 获取位置重叠的坐标轴
     */
    protected findOverlayAxis(axisProperty: IAxisProperty): IAxisProperty | undefined {
        const axes = this.chartModel.chartProperty.axes?.axisList;
        const axis = axes?.find(axis => axis.position === axisProperty.position);

        // 删除的坐标轴不做重叠样式统一
        if (axis && !axis.isDelete) {
            return axis;
        }
    }
}

export class DoubleValueAxis extends Axis {
    protected getXScale(): IChartScale | undefined {
        let xScale = super.getXScale();
        const { xAxisProperty: xAxis, subChart } = this;
        if (!subChart || !xAxis) return;

        const { logBase } = xAxis.scaling || {};
        xScale = {
            ...xScale,
            type: logBase ? 'log' : undefined,
        };

        const { scale, ticks } = subChart.xAxis || {};
        if (scale) {
            const { max, min, majorUnit } = scale;
            xScale = {
                ...xScale,
                max,
                min,
                maxLimit: max,
                minLimit: min,
                ticks,
            };
        }

        return xScale;
    }
}
